import React, { useCallback, useEffect, useState } from 'react'
import classnames from 'classnames'
import { Button } from 'antd'
import { identityLaunchPromise } from 'apis/FitOnCareAPI'
import verifyWithClearButtonIcon from './img/ic-verify-with-clear-button.svg'
import notEligibleIcon from '../img/ic-not-eligible.svg'
import { useI18n } from 'contexts/AuthContext'
import ContentHeader from '../ContentHeader'
import { logBrazeEvent } from 'utils/brazeInit'
import { logAmplitudeEvent } from 'utils/amplitudeInit'
import ButtonMailTo from 'components/ButtonMailTo/index'
import { getAutoLoginParams } from 'utils/basic'
import { getFitonUserId, getUserEmail } from 'utils/authLocalStorage'
import { USE_NATIVE_MCA_SIGNUP } from 'config'
import classes from './verifyWithClearFailure.module.scss'

export const useVerifyWithClear = () => {
  const [redirectUrl, setRedirectUrl] = useState('')
  const [urlLoaded, setUrlLoaded] = useState(false)

  const identityLaunch = useCallback(async () => {
    const resp = await identityLaunchPromise()
    if (resp.code === 200) {
      const baseUrl = resp.data?.redirectUrl || ''
      // Step 4 (docs/fiton-care-web-native-signup-login-plan.md): sign the
      // CLEAR redirect with u/e/t/h (getAutoLoginParams — the same
      // convention Eligible.js/ThanksForYourInterest.js/AlignmentFailed.js
      // already use to hand off back to fiton-web) so that once CLEAR's
      // identity-verification round-trip completes, fiton-web can
      // auto-login whichever device lands there itself. No changes needed
      // on the FitOn Care side of the return — this is the only touch point.
      // Gated behind the same USE_NATIVE_MCA_SIGNUP backdoor (config.js) as
      // Welcome.js/AlreadyEnrolled.js: false leaves the redirect untouched,
      // exactly as it was before this migration.
      if (USE_NATIVE_MCA_SIGNUP && baseUrl) {
        const separator = baseUrl.includes('?') ? '&' : '?'
        setRedirectUrl(
          `${baseUrl}${separator}${getAutoLoginParams({
            fitOnUserId: getFitonUserId(),
            fitOnEmail: getUserEmail(),
          })}`
        )
      } else {
        setRedirectUrl(baseUrl)
      }
    }
    setUrlLoaded(true)
  }, [])

  useEffect(() => {
    identityLaunch()
  }, [identityLaunch])

  return { redirectUrl, urlLoaded }
}

export const VerifyWithClearContent = (props) => {
  const { onBack, icon, title, text, onNext, urlLoaded, children } = props
  const { i18n } = useI18n()

  return (
    <div className={classnames('info-sec')}>
      <ContentHeader {...props} onBack={onBack} hideBack={true} />
      <div className='info-content'>
        <img src={icon} alt='' className='top-icon' />
        <div className='title-pns'>{i18n(title)}</div>
        <div
          className='subtitle'
          style={{
            marginBottom: '10px',
            marginTop: '17px',
            marginLeft: '0',
            marginRight: '0',
            fontFamily: 'AktivGrotesk',
            lineHeight: '24px',
          }}
        >
          {i18n(text)}
        </div>
        {children}
        <Button
          className='btn-cancel full-width-btn'
          style={{ marginTop: '32px', color: '#333333', width: '100%' }}
          onClick={onNext}
          disabled={!urlLoaded}
        >
          <img
            src={verifyWithClearButtonIcon}
            alt=''
            style={{ marginRight: '16px' }}
          />
          {i18n('Continue with Clear')}
        </Button>
      </div>
    </div>
  )
}

const VerifyWithClearFailure = (props) => {
  const { sku } = props
  const { redirectUrl, urlLoaded } = useVerifyWithClear()
  const { i18n } = useI18n()

  useEffect(() => {
    logBrazeEvent(`User Denied: Identity - ${sku}`)
    logAmplitudeEvent('Screen View: Onboarding - Not Available', {
      Reason: 'Identity',
      Detail: 'verification_failure',
      sku,
    })
  }, [sku])

  const onBack = (e) => {
    props.setRemoveEventListener(false)
    props.onBack(e)
  }
  const onNext = () => {
    if (redirectUrl) {
      window.location.href = redirectUrl
    }
  }

  return (
    <div className={classes.styles}>
      <VerifyWithClearContent
        {...props}
        icon={notEligibleIcon}
        title={'Verification failure'}
        text={
          'CLEAR was unable to verify your identity online. Please contact them via phone or email to complete the required verification.'
        }
        onNext={onNext}
        onBack={onBack}
        urlLoaded={urlLoaded}
      >
        <div
          className={'phone'}
          style={{ marginTop: '24px', marginBottom: '4px' }}
        >
          {i18n('Phone: 1-855-CLEARME (253-2763)')}
        </div>
        <div className={'email'}>
          {i18n('Email: ')}
          <ButtonMailTo
            email='memberservices@clearme.com'
            label='memberservices@clearme.com'
          />
        </div>
      </VerifyWithClearContent>
    </div>
  )
}

export default VerifyWithClearFailure
